 /* Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { AdminPanelSettingsOutlined, LoginOutlined, LogoutOutlined, PasswordOutlined } from "@mui/icons-material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AllOutOutlinedIcon from "@mui/icons-material/AllOutOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import WavesOutlinedIcon from "@mui/icons-material/WavesOutlined";
import SyncDisabledOutlinedIcon from "@mui/icons-material/SyncDisabledOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import { IKpi } from "../../src/store/api/admin/types";

import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import MailIcon from '@mui/icons-material/Mail';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FolderIcon from '@mui/icons-material/Folder';
import ErrorIcon from '@mui/icons-material/Error';
import LabelIcon from '@mui/icons-material/Label';
import SettingsIcon from '@mui/icons-material/Settings';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';

export const SEC = 1000;
export const MIN = 60 * SEC;
export const HOUR = 60 * MIN;
export const DAY = 24 * HOUR;
export const DEFAULT_IDLE_TIMEOUT_MSECS = 15 * MIN; // 15 minutes
export const IDLE_PROMPT_WAIT_MSECS = 2 * MIN; // 2 minutes
const DEFAULT_POLLING_FREQUENCY_MSECS = 30 * SEC; // 30 seconds
export const APP_BAR_TEXT = "AMS";
export const GEO_REDUNDANCY_PATH = "geo-redundancy";

export const JAVA_MAX_VALUE = 2147483647;

export const DASHBOARDS: { [name: string]: string } = {
  LTE_SITE: "lte_site",
  LTE_CELL: "lte_cell",
  NR_SITE: "nr_site",
  NR_CELL: "nr_cell",
  STORAGE: "storage",
};

export const performanceKeys = [
  {
    key: "pmcollection",
    label: "PM COLLECTION",
  },
  {
    key: "kpiStatus",
    label: "KPI SYNC STATUS",
  },
  {
    key: "intervals",
    label: "INTERVALS",
  },
  {
    key: "lastPmCollected",
    label: "LAST RECEIVED FILE",
  },
];

export interface IApiInfo {
  BASE_URL: string;
  POLLING_INTERVAL: number;
  ENDPOINTS: {
    [name: string]: {
      path: string | any;
      //args:string;
      pollingInterval?: number;
    };
  };
}

export const API: IApiInfo = {
  //create varibale that can take a value
  BASE_URL: "/",
  POLLING_INTERVAL: DEFAULT_POLLING_FREQUENCY_MSECS, // Default polling is every 30 seconds
  ENDPOINTS: {
    NODES: { path: "v1/vsnet_api_device/devices", pollingInterval: 10 * SEC },
    NODESCOUNTS: { path: "v1/vsnet_api_device/devices/counts", pollingInterval: 10 * SEC },
    ALLDEVICES: { path: "v1/vsnet_api_device/alldevices", pollingInterval: 30 * SEC },
    ALARMS: { path: "v1/vsnet_api_event/alarms", pollingInterval: 10 * SEC },
    EVENTS: { path: "v1/vsnet_api_event/events", pollingInterval: 10 * SEC },
    CONDITIONS: { path: "v1/vsnet_api_event/conditions", pollingInterval: 10 * SEC },
    TASKS: { path: "tasks" },
    TEMPLATES: { path: "v1/tasks/templates", pollingInterval: 10000 },
    CUSTOM_AUDIT_TEMPLATES: { path: "v1/tasks/custom-audit/templates", pollingInterval: 10000 },
    EXPORTTEMP: { path: "v1/tasks/templates/export" },
    CUSTOM_AUDIT_TEMPLATES_EXPORT: { path: "v1/tasks/custom-audit/templates/export" },
    IMPORTTEMPLATE: { path: "v1/tasks/templates/import" },
    SCHEDULEDTASKS: { path: "v1/tasks/alltasks", pollingInterval: 5000 },
    TASKLOGS: { path: "v1/tasks/task/log", pollingInterval: 5 * 1000 },
    RUNTASK: { path: "v1/tasks/task/execution" },
    DELETETASK: { path: "v1/tasks/delete" },
    IPINTERFACE: { path: "v1/vsnet_api_device/getLanDevices", pollingInterval: 10000 },
    LOGIN: { path: "v1/login" },
    LOGOUT: { path: "v1/logoutAudit" },
    VALIDATE: { path: "v1/validate" },
    REFRESH: { path: "v1/refresh" },
    ALARMCONDITION: { path: "&isAlarm=true" },
    PAGESIZEURLARG: { path: "&size=" },
    PAGENUMBERURLARG: { path: "page=" },
    RADIONODES: { path: "v1/vsnet_api_device/getRnList", pollingInterval: 10000 },
    NRCELLS: { path: "v1/vsnet_api_device/getNrCells", pollingInterval: 10000 },
    LTECELLS: { path: "v1/vsnet_api_device/getLteCells", pollingInterval: 10000 },
    LTEZONECELLS: { path: "v1/vsnet_api_device/getLteZones", pollingInterval: 10000 },
    NGZONECELLS: { path: "v1/vsnet_api_device/getNgZones", pollingInterval: 10000 },
    NGX2CONNECTIONS: { path: "v1/vsnet_api_device/getNgX2ConnectionsList" },
    LTEX2CONNECTIONS: { path: "v1/vsnet_api_device/getLteX2ConnectionsList" },
    DEVICEBYKEY: { path: "v1/vsnet_api_device/getDeviceByKey", pollingInterval: 4000 },
    SWVERSION: { path: "v1/vsnet_api_device/getSwVersions" },
    RFFEEDS: { path: "v1/vsnet_api_device/getRfFeeds" },
    RN620S: { path: "v1/vsnet_api_device/getRn620s" },
    EVENTCONFIGURATION: { path: "v1/admin/events-configuration" },
    SYSTEMOPTIONS: { path: "v1/admin/appconfig" },
    PROFILE: { path: "v1/admin/profile" },
    USER: { path: "v1/admin/users" },
    USERSELF: { path: "v1/admin/user/self" },
    CLEARFAILEDLOGIN: { path: "v1/admin/user/self/clear-failed-login" },
    CREATEUSER: { path: "v1/admin/user" },
    UPDATEUSER: { path: "v1/admin/user" },
    CHANGEPASSWORD: { path: "v1/admin/changePassword" },
    ACCESSACONTROLSETTINGS: { path: "v1/admin/access-control-settings" },
    UPDATEACCESSCONTROLSETTINGS: { path: "v1/admin/access-control-settings" },
    DELETEUSER: { path: "v1/admin/user" },
    AUDIT: { path: "v1/admin/audit" },
    GOLDEN_AUDIT_DEFINITIONS: { path: "v1/audit/definitions" },
    GOLDEN_AUDIT_TEMPLATES: { path: "v1/audit/templates" },
    GOLDEN_AUDIT_VIOLATIONS: { path: "v1/audit" },
    DEVICECONFIG: { path: "v1/device_config/set" },
    CONFIGALERTS: { path: "v1/device_config/configAlerts", pollingInterval: 10000 },
    CONFIGSTATUS: { path: "v1/device_config", pollingInterval: 10000 },
    EXTERNALAUTHENTICATION: { path: "v1/admin/server/authentication/external" },
    INVENTORY: { path: "v1/vsnet_api_device/getInventory" },
    ADMIN: { path: "v1/admin" },
    DAS_RADIONODES: { path: "v1/vsnet_api_device/inventory/das/radionodes" },
    DAS_INVENTORY: { path: "v1/vsnet_api_device/inventory/das/remoteequipment" },
    DAS_INVENTORY_GRAPH: { path: "v1/vsnet_api_device/inventory/das/graph" },
    GROUPS: { path: "v1/admin/groups" },
    GROUPSCREATE: { path: "v1/admin/groups/create" },
    REMOVEDEVICE: { path: "v1/device_config/delete" },
    DEVICECOUNTS: { path: "v1/vsnet_api_device/deviceCounts", pollingInterval: 4000 },
    RADIONODESSTATS: { path: "v1/vsnet_api_device/inventory/radionodes/stats", pollingInterval: 10000 },
    INVENTORY_RADIONODES: { path: "v1/vsnet_api_device/inventory/radionodes" },
    INVENTORY_CELLS: { path: "v1/vsnet_api_device/inventory/cells" },
    INVENTORY_CELLS_SUMMARY: { path: "v1/vsnet_api_device/inventory/cells/summary" },
    INVENTORY_EXPORT: { path: "v1/vsnet_api_device/inventory/export" },
    INVENTORY_RADIONODE_MODELS: { path: "v1/vsnet_api_device/inventory/radionode/models" },
    INVENTORY_STATUS: { path: "v1/vsnet_api_device/inventory/status" },
    DRAWERCOUNTS: { path: "v1/vsnet_api_device/drawerCounts" },
    CREATETASK: { path: "v1/tasks/create" },
    UPDATETASK: { path: "/v1/tasks/update" },
    EXPORTAUDITLOGS: { path: "v1/admin/exportauditlogs" },
    EXPORTEVENTS: { path: "v1/vsnet_api_event/exportevents" },
    EXPORTALARMS: { path: "v1/vsnet_api_event/exportalarms" },
    RUNONDEMANDTASK: { path: "v1/tasks/task/runondemand" },
    SITEGROUPS: { path: "v1/vsnet_api_device/sitegroups", pollingInterval: 10 * SEC },
    NORTHBOUND: { path: "v1/admin/snmp-manager-groups", pollingInterval: 10000 },
    SNMPGROUP: { path: "v1/admin/snmp-manager-group" },
    CREATESNMPGROUP: { path: "v1/admin/create-snmp-group" },
    SNMPAGENT: { path: "v1/admin/snmp-agent" },
    TRAPRECIEVE: { path: "v1/admin/trap-receive" },
    EMAILGROUPS: { path: "v1/admin/emailgroups" },
    EMAILGROUP: { path: "v1/admin/emailgroup" },
    SNMTPEMAIL: { path: "v1/admin/email-smtp" },
    APPLYTEMPLATE: { path: "v1/device_config/apply" },
    ALLTASKLOGS: { path: "v1/tasks/task/logs", pollingInterval: 5 * 1000 },
    REFRESHFULLDEVICE: { path: "v1/device_config/refresh" },
    VERSION: { path: "v1/admin/version" },
    GEOREGIONS: { path: "v1/admin/georegions" },
    TEMPLATELOGS: { path: "v1/tasks/templates/logs", pollingInterval: 10000 },
    SNMPEMAILGROUPS: { path: "v1/admin/groups-name", pollingInterval: 20 * SEC },
    RFFEEDSLATEST: { path: "v1/vsnet_api_device/getRfFeedsLatest" },
    RNCELLDETAILS: { path: "v1/vsnet_api_device/rnCellConfig" },
    RNCELLCONFIGS: { path: "v1/vsnet_api_device" },
    CUSTOMEVENTS: { path: "v1/admin/customEvents" },
    CUSTOMEVENT: { path: "v1/admin/customEvent" },
    USERSFILEMGMT: { path: "v1/filemgmt/users" },
    EXTERNALUSERSFILEMGMT: { path: "v1/filemgmt/externalusers" },
    SYNCOPTIONS: { path: "v1/filemgmt/sync-options" },
    SSHCLIACCESS: { path: "v1/filemgmt/ssh-cli-access" },
    DULIST: { path: "v1/vsnet_api_device/DU" },
    SSOENABLED: { path: "v1/sso/enabled" },
    MIGRATION: { path: "v1/migration", pollingInterval: 15 * SEC },
    EXPORTCONDITIONS: { path: "v1/vsnet_api_event/exportconditions" },
    REFRESHPARTIALDEVICE: { path: "v1/device_config/partialrefresh" },
    TASKLOGDETAILS: { path: "v1/tasks/task/logdetails" },
    EXPORTDEVICES: { path: "v1/vsnet_api_device/exportdevices" },
    SYSLOGS: { path: "v1/admin/syslog" },
    KPISTORAGE: { path: "v1/filemgmt/kpi-storage", pollingInterval: 20000 },
    PVCSTORAGE: { path: "v1/filemgmt/pvc-storage", pollingInterval: 20000 },
    DBSTORAGE: { path: "v1/filemgmt/db-storage", pollingInterval: 20000 },
    PMFILESTORAGE: { path: "v1/filemgmt/pmfile-storage", pollingInterval: 20000 },
    SUPERSETTOKEN: { path: "v1/vsnet_api_device/superset/token" },
    GETCELLRN: { path: "v1/vsnet_api_device/getRN" },
    GETCELLDU: { path: "v1/vsnet_api_device/getDU" },
    REPORTLIST: { path: "v1/tasks/auditreport/reportlist" },
    ALLREPORTS: { path: "v1/tasks/auditreport/allreports", pollingInterval: 20000 },
    DOWNLOADREPORT: { path: "v1/tasks/auditreport/report" },
    REPORTTASKLIST: { path: "v1/tasks/auditreport/alltasks" },
    FILESYSTEMLIST: { path: "v1/filemgmt/fileSystemList" },
    DOWNLOADFILE: { path: "v1/filemgmt/downloadFile" },
    RETENTIONPOLICIES: { path: "/v1/filemgmt/get-retention", pollingInterval: 20000 },
    CELLKPIDATA: { path: "/v1/vsnet_kpis" },
    TESTEMAIL: { path: "v1/admin/snmp-agent/test-email" },
    CONFIGUREPRIMARY: { path: "v1/redundancy/configure-primary" },
    GETCONFIGURATION: { path: "v1/redundancy/configuration", pollingInterval: 10000 },
    RESETACTIVE: { path: "/v1/redundancy/reset" },
    FAILOVER: { path: "/v1/redundancy/failover" },
    STATUS: { path: "/v1/redundancy/open/status", pollingInterval: 10000 },
    TESTCONFIGURATION: { path: "/v1/redundancy/test-configuration" },
    LTERNCELLNAME: { path: "/v1/vsnet_api_device/cellname" },
    ISSTANDBY: { path: "/v1/redundancy/open/isStandby" },
    ACCESSCONTROLSETTINGS: { path: "/v1/admin/access-control-settings" },
    PARTIALREFRESHRN: { path: "/v1/device_config/partialrefresh/rn" },
    EXTERNALPERFORMANCEDATA: { path: "/v1/vsnet_kpis/performance/region" },
    NRPERFORMANCEDATA: { path: "/v1/vsnet_kpis/performance/region/NR" },
    ACTIVITYPERFORMANCEDATA: { path: "/v1/vsnet_kpis/performance/device" },
    ADDOFFSITE: { path: "v1/device_config/offlinedevice/bulkUpload" },
    ADDDEVICE: { path: "v1/device_config/offlinedevice/addDevice" },
    DASDISCOVERY: { path: "v1/device_config/offlinedevice/triggerDiscovery" },
    DAS_SUPPORTED_MODELS: { path: "v1/device_config/offlinedevice/supported-models" },
    DAS_FEATURE_FLAGS: { path: "v1/device_config/offlinedevice/feature-flags" },
    DATAPOLLING: { path: "/v1/admin/datapollerproperties" },
    DEPLOYMENT: { path: "/v1/vsnet_api_device/deployment" },
    PASSWORDSETTINGS: { path: "/v1/password/settings" },
    THRESHOLDCROSSINGSETTINGPROFILE: { path: "v1/performance/profiles" },
    THRESHOLDCROSSINGSETTINGACTION: { path: "v1/performance/actions" },
    THRESHOLDCROSSINGSETTINGSCHEDULE: { path: "v1/performance/schedules" },
    THRESHOLDCROSSINGSETTINGRULE: { path: "v1/performance/rules" },
    THRESHOLDCROSSINGPROFILE: { path: "v1/performance/profiles/device" },

    NOTIFICATIONGROUPS: { path: "v1/admin/notificationgroups" },
    THRESHOLDDEVICES: { path: "v1/performance/profiles" },
    ALLDEVICESBYGROUP: { path: "v1/vsnet_api_device/allDevices" },
    // Device notes (device detail & configuration)
    NOTES: { path: "v1/vsnet_api_device/notes" },
    LABELS: { path: "v1/vsnet_api_device/devicelabels" },
    ADDLABELTODEVICE: { path: "v1/vsnet_api_device/assignLabels" },
    ALARMTICKET: { path: "v1/vsnet_api_event/alarm/ticket" },
    ADDDEVICESTOLABEL: { path: "v1/vsnet_api_device/devices/labels/add" }, // label management 
    REMOVELABELFROMDEVICES: { path: "v1/vsnet_api_device/devices/labels/remove" }, // label management
    THRESHOLDALERT: { path: "v1/performance/alert", pollingInterval: 10000 },
    THRESHOLDALERTEXPORT: { path: "v1/performance/alert/export" },
    ALARMCOUNT: { path: "v1/vsnet_api_device/alarmcount", pollingInterval: 60000 },
    CELLSCOUNTS: { path: "v1/vsnet_api_device/dashboard/cells/counts", pollingInterval: 60000 },
    TOPOFFENDERS: { path: "v1/vsnet_api_device/dashboard/offenders", pollingInterval: 60000 },
    TOPOFFENDERSEXPORT: { path: "v1/vsnet_api_device/dashboard/offenders/export" },
    DISCONNECTEDRADIONODE: { path: "v1/vsnet_api_device/inventory/radionodes/disconnectedPercentageByDevice", pollingInterval: 10 * SEC },
    EXPORTDISCONNECTEDRADIONODES: { path: "v1/vsnet_api_device/inventory/radionodes/disconnectedPercentageByDevice/export" },
  },
};

export const API_SLICE_TAGS: { [name: string]: string } = {
  NODES: "NODES",
  NODESCOUNTS: "NODESCOUNTS",
  ALLDEVICES: "ALLDEVICES",
  ALARMS: "ALARMS",
  EVENTS: "EVENTS",
  CONDITIONS: "CONDITIONS",
  TASKS: "TASKS",
  TEMPLATES: "TEMPLATES",
  CUSTOM_AUDIT_TEMPLATES: "CUSTOM_AUDIT_TEMPLATES",
  SCHEDULEDTASKS: "SCHEDULEDTASKS",
  IPINTERFACE: "IPINTERFACE",
  RADIONODES: "RADIONODES",
  NRCELLS: "NRCELLS",
  ZONECELLS: "ZONECELLS",
  LTECELLS: "LTECELLS",
  NGX2CONNECTIONS: "NGX2CONNECTIONS",
  LTEX2CONNECTIONS: "LTEX2CONNECTIONS",
  DEVICEBYKEY: "DEVICEBYKEY",
  EVENTCONFIGURATION: "EVENTCONFIGURATION",
  EVENTSBYKEY: "EVENTSBYKEY",
  ALARMSBYKEY: "ALARMSBYKEY",
  SWVERSION: "SWVERSION",
  SYSTEMOPTIONS: "SYSTEMOPTIONS",
  PROFILE: "PROFILE",
  PROFILES: "PROFILES",
  USER: "USERS",
  ADMIN: "ADMIN",
  REGIONS: "REGIONS",
  ACCESSCONTROLSETTINGS: "ACCESSCONTROLSETTINGS",
  AUDIT: "AUDIT",
  GOLDEN_AUDIT_DEFINITIONS: "GOLDEN_AUDIT_DEFINITIONS",
  DEVICECONFIG: "DEVICECONFIG",
  CONFIGALERTS: "CONFIGALERTS",
  CONFIGSTATUS: "CONFIGSTATUS",
  EXTERNALAUTHENTICATION: "EXTERNALAUTHENTICATION",
  INVENTORY: "INVENTORY",
  DAS_RADIONODES: "DAS_RADIONODES",
  DAS_INVENTORY: "DAS_INVENTORY",
  DAS_INVENTORY_GRAPH: "DAS_INVENTORY_GRAPH",
  INVENTORY_RADIONODES: "INVENTORY_RADIONODES",
  INVENTORY_CELLS: "INVENTORY_CELLS",
  INVENTORY_CELLS_SUMMARY: "INVENTORY_CELLS_SUMMARY",
  INVENTORY_RADIONODE_MODELS: "INVENTORY_RADIONODE_MODELS",
  INVENTORY_STATUS: "INVENTORY_STATUS",
  GROUPS: "GROUPS",
  DEVICECOUNTS: "DEVICECOUNTS",
  RADIONODESSTATS: "RADIONODESSTATS",
  DRAWERCOUNTS: "DRAWERCOUNTS",
  CREATETASK: "CREATETASK",
  UPDATETASK: "UPDATETASK",
  EXPORTAUDITLOGS: "EXPORTAUDITLOGS",
  EXPORTEVENTS: "EXPORTEVENTS",
  EXPORTALARMS: "EXPORTALARMS",
  GEOREGIONS: "GEOREGIONS",
  SITEGROUPS: "SITEGROUPS",
  NORTHBOUND: "NORTHBOUND",
  SNMPAGENT: "SNMPAGENT",
  TRAPRECIEVE: "TRAPRECIEVE",
  EMAILGROUPS: "EMAILGROUPS",
  REFRESHFULLDEVICE: "REFRESHFULLDEVICE",
  VERSION: "VERSION",
  SNMPEMAILGROUPS: "SNMPEMAILGROUPS",
  RFFEEDSLATEST: "RFFEEDSLATEST",
  CUSTOMEVENT: "CUSTOMEVENT",
  CUSTOMEVENTS: "CUSTOMEVENTS",
  USERSFILEMGMT: "USERSFILEMGMT",
  SYNCOPTIONS: "SYNCOPTIONS",
  SSHCLIACCESS: "SSHCLIACCESS",
  SSOENABLED: "SSOENABLED",
  MIGRATION: "MIGRATION",
  SYSLOGS: "SYSLOGS",
  KPISTORAGE: "KPISTORAGE",
  PVCSTORAGE: "PVCSTORAGE",
  DBSTORAGE: "DBSTORAGE",
  PMFILESTORAGE: "PMFILESTORAGE",
  REPORTLIST: "REPORTLIST",
  ALLREPORTS: "ALLREPORTS",
  DOWNLOADREPORT: "DOWNLOADREPORT",
  REPORTTASKLIST: "REPORTTASKLIST",
  FILESYSTEMLIST: "FILESYSTEMLIST",
  DOWNLOADFILE: "DOWNLOADFILE",
  RETENTIONDATA: "RETENTIONDATA",
  STATUS: "STATUS",
  CONFIGURATION: "CONFIGURATION",
  THRESHOLDCROSSINGSETTINPROFILE: "THRESHOLDCROSSINGSETTINPROFILE",
  THRESHOLDCROSSINGSETTINGACTION: "THRESHOLDCROSSINGSETTINGACTION",
  THRESHOLDCROSSINGSETTINGSCHEDULE: "THRESHOLDCROSSINGSETTINGSCHEDULE",
  THRESHOLDCROSSINGSETTINGRULE: "THRESHOLDCROSSINGSETTINGRULE",
  THRESHOLDALERT: "THRESHOLDALERT",
  NOTIFICATIONGROUPS: "NOTIFICATIONGROUPS",
  ALLDEVICESBYGROUP: "ALLDEVICESBYGROUP",
  LABELS: "LABELS",
  DISCONNECTEDRADIONODE: "DISCONNECTEDRADIONODE",
};

export const LOCAL_STORAGE: { [name: string]: string } = {
  TOKEN: "token",
  REFRESH_TOKEN: "refreshToken",
  TOKEN_EXPIRATION: "expiration",
  USER: "userName",
  ALLDEVICE: "allDevice",
  DISCONNECTED: "disconnected",
  MAINTENANCEMODE: "inMaintenanceMode",
  UNGROUPED: "ungrouped",
  PROFILE: "profile",
  CURRENTREGION: "currentRegion",
  ACTIVETAB: "ACTIVETAB",
  SELECTED_GEO_REGION_IDS: "selectedGeoRegionIds",
  SELECTED_NAV_ITEM: "selectedNavItem",
  JWTCOOKIE: "VSNETJWT",
  SSHACTIVITYCOOKIE: 'ssh_last_activity',
};

export interface IRouteInfo {
  Key: number;
  Path: string;
  Text: string;
  ShowInDrawer?: boolean;
  Icon?: any;
  permissionRequired?: string;
  isExternal?: boolean;
}

export interface IBandList {
  label: string;
  value: string;
}

let key = 0;
export const ROUTES: { [name: string]: IRouteInfo } = {
  HOME: { Key: key++, Path: "/", Text: "", ShowInDrawer: false },
  LOGIN: {
    Key: key++,
    Path: "login",
    Text: "Login",
    Icon: LoginOutlined,
    ShowInDrawer: false,
  },
  LOGOUT: {
    Key: key++,
    Path: "logout",
    Text: "Logout",
    Icon: LogoutOutlined,
    ShowInDrawer: false,
  },
  PASSWORD: {
    Key: key++,
    Path: "password",
    Text: "Change Password",
    Icon: PasswordOutlined,
    ShowInDrawer: false,
  },
  DASHBOARD: {
    Key: key++,
    Path: "",
    Text: "Dashboard",
    Icon: DashboardOutlinedIcon,
    ShowInDrawer: true,
  },
  NODES: {
    Key: key++,
    Path: "nodes",
    Text: "Nodes",
    Icon: AllOutOutlinedIcon,
    ShowInDrawer: true,
  },
  // SERVICES: {
  //   Key: key++,
  //   Path: "services",
  //   Text: "Services",
  //   Icon: CategoryOutlinedIcon,
  //   ShowInDrawer: true,
  // },
  TASKS: {
    Key: key++,
    Path: "tasks",
    Text: "Tasks",
    Icon: CategoryOutlinedIcon,
    ShowInDrawer: true,
  },
  DEVICEDETAILS: {
    Key: key++,
    Path: "device/:id",
    Text: "Device details",
    Icon: CategoryOutlinedIcon,
    ShowInDrawer: false,
  },
  SETTINGS: {
    Key: key++,
    Path: "settings",
    Text: "Settings",
    Icon: SettingsOutlinedIcon,
    ShowInDrawer: false,
  },
  ADMINISTRATION: {
    Key: key++,
    Path: "administration",
    Text: "Administration",
    Icon: AdminPanelSettingsOutlined,
    ShowInDrawer: true,
  },
  EXTERNALPERFORMANCE: {
    Key: key++,
    Path: "externalPerformance",
    Text: "Network Performance",
    Icon: CategoryOutlinedIcon,
    ShowInDrawer: false,
  },
};

export const NODEROUTES: { [name: string]: IRouteInfo } = {
  NODESPANEL: {
    Key: key++,
    Path: "",
    Text: "Devices",
    permissionRequired: "view_sn",
  },
  ALARMSPANEL: {
    Key: key++,
    Path: "alarms",
    Text: "Alarms",
    permissionRequired: "view_events",
  },
  EVENTSPANEL: {
    Key: key++,
    Path: "events",
    Text: "Events",
    permissionRequired: "view_events",
  },
  CONDITIONSPANEL: {
    Key: key++,
    Path: "conditions",
    Text: "Conditions",
    permissionRequired: "view_events",
  },
  INVENTORYPANEL: {
    Key: key++,
    Path: "inventory",
    Text: "Inventory",
    permissionRequired: "view_sn",
  },
  PERFORMANCEPANEL: {
    Key: key++,
    Path: "performance",
    Text: "Performance",
    permissionRequired: "view_events",
  },
};

export interface IDeviceGroupingInfo extends IRouteInfo {
  Parameter?: string;
  Models: string[];
  countValue?: number;
  countColor?: "error" | "default" | "success" | "warning" | "primary" | "secondary" | "info";
}

export const DEVICEGROUPS: { [name: string]: IDeviceGroupingInfo } = {
  ALLDEVICES: {
    Key: key++,
    Path: "",
    Text: "All Devices",
    Icon: AllOutOutlinedIcon,
    Models: [],
  },
  CUAPPLIANCE: {
    Key: key++,
    Path: "?device=cu",
    Parameter: "cu",
    Text: "CU Appliance",
    Models: ["CU", "vCU", "CU Appliance"],
  },
  NGSERVICENODES: {
    Key: key++,
    Path: "?device=ng",
    Parameter: "ng",
    Text: "NG Service Nodes",
    Models: ["NG", "NG*"],
  },
  SERVICENODES: {
    Key: key++,
    Path: "?device=sn",
    Parameter: "sn",
    Text: "Service Nodes",
    Models: ["Service Node", "SN", "SCRN*"],
  },
};

export const SERVICEROUTES: { [name: string]: IRouteInfo } = {
  // NODECONFIGURE: {
  //   Key: key++,
  //   Path: "",
  //   Text: "Configuration",
  //   ShowInDrawer: false,
  //   Icon: "Chip",
  // },
  ALARMSPANEL: {
    Key: key++,
    Path: "alarms",
    Text: "Alarms",
    ShowInDrawer: true,
    Icon: "Chip",
  },
  EVENTSPANEL: {
    Key: key++,
    Path: "events",
    Text: "Events",
    ShowInDrawer: true,
    Icon: "Chip",
  },
  CONDITIONSPANEL: {
    Key: key++,
    Path: "conditions",
    Text: "Conditions",
    ShowInDrawer: true,
    Icon: "Chip",
  },
  // TASKSPANEL: {
  //   Key: key++,
  //   Path: "tasks",
  //   Text: "Tasks",
  //   ShowInDrawer: false,
  //   Icon: "Chip",
  // },
};

export const CONFIGUREROUTES: { [name: string]: IRouteInfo } = {
  NODESUMMARY: {
    Key: key++,
    Path: "",
    Text: "Summary",
  },
  COMMISSIONING: {
    Key: key++,
    Path: "commissioning",
    Text: "Commissioning",
  },
  IPINTERFACE: {
    Key: key++,
    Path: "interfaces",
    Text: "Interfaces",
  },
  INVENTORY: {
    Key: key++,
    Path: "inventory",
    Text: "Inventory",
  },
  DASRADIONODES: {
    Key: key++,
    Path: "das-radionodes",
    Text: "Remotes",
  },
  HCMINFO: {
    Key: key++,
    Path: "hcm-info",
    Text: "Controller Info",
  },
  DCUINFO: {
    Key: key++,
    Path: "dcu-info",
    Text: "Controller Info",
  },
  CONTROLLERINFO: {
    Key: key++,
    Path: "controller-info",
    Text: "Controller Info",
  },
  DASINVENTORY: {
    Key: key++,
    Path: "das-inventory",
    Text: "Inventory",
  },
  DASTOPOLOGY: {
    Key: key++,
    Path: "das-topology",
    Text: "Topology",
  },
  RN620: {
    Key: key++,
    Path: "rn620s",
    Text: "620",
  },
  RFFEED: {
    Key: key++,
    Path: "rffeeds",
    Text: "RF Feeds",
  },
  RFFEEDLATEST: {
    Key: key++,
    Path: "rffeedslatest",
    Text: "RF Feeds",
  },
  RADIONODE: {
    Key: key++,
    Path: "radionode",
    Text: "Radio Nodes",
  },
  NRCELL: {
    Key: key++,
    Path: "nrcell",
    Text: "NR Cell",
  },
  ZONES: {
    Key: key++,
    Path: "zones",
    Text: "Zones",
  },
  LTECELL: {
    Key: key++,
    Path: "ltecell",
    Text: "LTE Cell",
  },
  NGX2CONNECTION: {
    Key: key++,
    Path: "ngx2connection",
    Text: "X2 Connections",
  },
  LTEX2CONNECTION: {
    Key: key++,
    Path: "ltex2connection",
    Text: "X2 Connections",
  },
  SSH: {
    Key: key++,
    Path: "ssh",
    Text: "SSH Terminal",
  },
  DU: {
    Key: key++,
    Path: "du",
    Text: "DUs",
  },
  SUPERSET: {
    Key: key++,
    Path: "performance",
    Text: "Performance",
  },
  FILES: {
    Key: key++,
    Path: "files",
    Text: "Files",
  },
  SNMPDETAILS: {
    Key: key++,
    Path: "snmpdetails",
    Text: "Connection Details",
  },
  LOCATION: {
    Key: key++,
    Path: "location",
    Text: "Location",
  },
  WEB_TERMINAL: {
    Key: key++,
    Path: "web-terminal",
    Text: "Web Terminal",
    isExternal: true,
  },
};

export const DEVICELIST: { [name: string]: IDeviceGroupingInfo } = {
  ALLDEVICES: {
    Key: key++,
    Path: "",
    Text: "All Devices",
    Icon: AllOutOutlinedIcon,
    Models: [],
  },
  DISCONNECTED: {
    Key: key++,
    Path: "snconnected",
    Parameter: "status='19'",
    Text: "Disconnected",
    Icon: SyncDisabledOutlinedIcon,
    Models: ["DIS", "OFF"],
  },
  //Provisioning
  ALL: {
    Key: key++,
    Path: "provisioning",
    Parameter: "provisioning",
    Text: "All",
    Icon: WavesOutlinedIcon,
    Models: ["ALL", "PR"],
  },
  UNPROVISIONING: {
    Key: key++,
    Path: "unprovisioning",
    Parameter: "sn",
    Text: "UnProvisioning",
    Models: ["UNP"],
  },
  CSG: {
    Key: key++,
    Path: "csg",
    Parameter: "csg",
    Text: "CSG",
    Models: ["CSG"],
  },
  STAGE: {
    Key: key++,
    Path: "stage",
    Parameter: "stage",
    Text: "Stage",
    Models: [],
  },
};

export const ADMINISTRATIONLIST: { [name: string]: IRouteInfo } = {
  ACCESSCONTROL: {
    Key: key++,
    Path: "",
    Text: "Access Control",
    ShowInDrawer: false,
    permissionRequired: "user_management",
    Icon: AdminPanelSettingsIcon,
  },
  AUDITTRAIL: {
    Key: key++,
    Path: "audit-trail",
    Text: "Audit Trail",
    ShowInDrawer: false,
    Icon: RestoreOutlinedIcon,
  },
  NORTHBOUNDINTERFACE: {
    Key: key++,
    Path: "northbound-interface",
    Text: "Northbound Interface",
    ShowInDrawer: false,
    Icon:ApiOutlinedIcon,
  },
  EMAIL: {
    Key: key++,
    Path: "email",
    Text: "Email",
    ShowInDrawer: false,
    Icon: MailIcon,
  },
  FILEMANAGEMENT: {
    Key: key++,
    Path: "file-management",
    Text: "File Management",
    ShowInDrawer: false,
    Icon: FolderIcon,
  },
  FAULTMANAGEMENT: {
    Key: key++,
    Path: "fault-management",
    Text: "Fault Management",
    ShowInDrawer: false,
    Icon: ErrorIcon,
  },
  SERVERSETTINGS: {
    Key: key++,
    Path: "server-settings",
    Text: "Server Settings",
    ShowInDrawer: false,
    Icon:SettingsIcon
  },
  MIGRATION: {
    Key: key++,
    Path: "device-migration",
    Text: "Device Migration",
    ShowInDrawer: false,
    Icon:SwapHorizOutlinedIcon,
  },
  REDUNDANCY: {
    Key: key++,
    Path: GEO_REDUNDANCY_PATH,
    Text: "Redundancy",
    Icon: PublishedWithChangesIcon,
    ShowInDrawer: false,
  },
  TRISYNC: {
    Key: key++,
    Path: "tri-sync",
    Text: "Trisync",
    ShowInDrawer: false,
  },
  PERFORMANCE: {
    Key: key++,
    Path: "Performance",
    Text: "Performance",
    Icon: SpeedOutlinedIcon,
  },
  LABELMANAGEMENT: {
    Key: key++,
    Path: "label-management",
    Text: "Label Management",
    ShowInDrawer: false,
    Icon:LabelIcon
  },
  /*
  DATABASESETTINGS: {
    Key: key++,
    Path: "database-settings",
    Text: "Database settings",
    ShowInDrawer: false,
  },
  USERDEFINEDDIRECTORIES: {
    Key: key++,
    Path: "Userdefined-directories",
    Text: "User defined directories",
    ShowInDrawer: false,
  },
  GEOGRAPHICALMAPS: {
    Key: key++,
    Path: "geographical-maps",
    Text: "Geographical maps",
    ShowInDrawer: false,
  },
  PERFORMANCECONFIGURATION: {
    Key: key++,
    Path: "performance-configuration",
    Text: "Performance configuration",
    ShowInDrawer: false,
  },
  SERVICEPROVISIONING: {
    Key: key++,
    Path: "service-provisioning",
    Text: "Service provisioning",
    ShowInDrawer: false,
  },
*/
};

export const TRAPSEVERITYLIST: Array<string> = ["Clear", "Critical", "Major", "Minor", "Warning", "Info"];
export const SEVERITYLIST: Array<string> = ["Critical", "Major", "Minor", "Warning", "Info", "Cleared", "Indeterminate"];
export const VERSIONLIST: Array<string> = ["9.2.0", "10.4.0", "23.1", "23.2", "23.3"];
export const ALARMSEVERITYLIST: Array<string> = ["Critical", "Major", "Minor", "Warning", "Info"];
export const STATUSLIST: Array<string> = [
  "FAULT",
  "IS-NETMON",
  "IS-IDLE",
  "IS-NORMAL",
  "IS-DEGRADED",
  "OOS-DISABLED",
  "OOS-INHERITED",
  "OOS-NOTPRESENT",
  "OOS-UNPROVISIONED",
];
export const statusRadioNodes = [
  "IS-IDLE",
  "IS-NORMAL",
  "IS-DEGRADED",
  "OOS-DISABLED",
  "OOS-SYNCING",
  "OOS-PROVING",
  "OOS-UNPROVISIONED",
  "OOS-INHERITED",
  "OOS-AUTHENTICATING"
];
export const statusRNColumns = [
  "IS-IDLE",
  "IS-NORMAL",
  "IS-DEGRADED",
  "OOS-DISABLED",
  "OOS-SYNCING",
  "OOS-PROVING",
  "OOS-UNPROVISIONED",
  "OOS-AUTHENTICATING",
  "OOS-INDETERMINATE",
  "OOS-INIT",
  "OOS-INHERITED",
];
export const PROTOCOLLIST: Array<string> = ["FTP", "SMTP", "SNMP", "TCP", "UDP"];
export const NODETYPES: Array<string> = ["SN", "CU", "CUAPP", "IntegratedUnit"];
// export const NODETYPES:Ioptions[] = [
//   { value: "SN", text: "SN" },
//   { value: "CU", text: "CU" },
//   { value: "CUAPP", text: "CUAPP" },
//   { value: "IntegratedUnit", text: "IntegratedUnit" },
// ];

export interface IOpCodeGroups {
  [name: string]: {
    description: string;
    isAdmin?: boolean;
    required?: boolean;
    child?: string;
    parent?: string;
  };
}

export const OP_CODE_GROUPS: IOpCodeGroups = {
  system_admin: { description: "System Administration", isAdmin: true },
  user_management: { description: "User Management", isAdmin: true },
  configure_profile: { description: "Configure Profile", isAdmin: true },
  manage_topology: { description: "Manage Topology", isAdmin: false, child: "view_topology" },
  view_topology: { description: "View Topology", isAdmin: false, required: true, parent: "manage_topology" },
  manage_sn: { description: "Manage Node", isAdmin: false, child: "view_sn" },
  view_sn: { description: "View Node", isAdmin: false, parent: "manage_sn", required: true },
  task_management: { description: "Manage Tasks", isAdmin: false, child: "view_tasks" },
  view_tasks: { description: "View Tasks", isAdmin: false, parent: "task_management" },
  // acknowledge_events: { description: "Acknowledge Events", isAdmin: false, child: "view_events" },
  view_events: { description: "View Events", isAdmin: false, parent: "acknowledge_events" },
  performance_management: { description: "Manage Performance", isAdmin: false, child: "view_performance" },
  view_performance: { description: "View Performance", isAdmin: false, parent: "performance_management" },
  // manage_fc_tca: { description: "Manage Threshold Notifications", isAdmin: false, child: "view_fc_tca" },
  // view_fc_tca: { description: "View Threshold Notifications", isAdmin: false, parent: "manage_fc_tca" },
  view_profile: { description: "View Profile", isAdmin: false, required: true },
  manage_label_management: { description: "Manage Label Management", isAdmin: false, child: "view_label_management" },
  view_label_management: { description: "View Label Management", isAdmin: false, required: true, parent: "manage_label_management" },
};

export const DATEFORMATS: { [key: string]: string } = {
  ZONE: "YYYY-MM-DDTHH:mm:ss.SSSZ",
  AMPM: "MM/DD/YYYY HH:mm:ss A",
  TS: "YYYY-MM-DD HH:mm:ss",
  TSN: "MMM DD, YYYY, HH:mm:ss",
};

export const DIALOGOPENFOR = {
  CLEARKPIALERTTHRESHOLD: "CLEARKPIALERTTHRESHOLD",
  LANDEVICE: "LANDEVICE",
  IPINTERFACE: "IPINTERFACE",
  RFFEED: "RFFEED",
  PROFILE: "PROFILE",
  PROFILE_SAVE: "PROFILE_SAVE",
  USER: "USER",
  ACTION: "ACTION",
  CONNECTRFFEED: "CONNECTRFFEED",
  ADDSCHEDULEDTASK: "ADDSCHEDULEDTASK",
  REBOOTTASK: "REBOOTTASK",
  GROUPS: "GROUPS",
  ADDGROUP: "ADDGROUP",
  GROUPSTODEVICE: "GROUPSTODEVICE",
  DELETETASK: "DELETE TASK",
  RUNTASK: "RUN TASK",
  SOFTWAREDOWNLOADANDUPDATED: "SOFTWAREDOWNLOADANDUPDATED",
  ZONE: "ZONE",
  ACTIONTASK: "ACTIONTASK",
  ACTIONGENERALTASK: "ACTIONGENERALTASK",
  SITEGROUPS: "SITEGROUPS",
  SITEGROUPSTODEVICE: "SITEGROUPSTODEVICE",
  ALARMFORWARDING: "ALARMFORWARDING",
  LTEX2CONNECTION: "LTEX2CONNECTION",
  NGX2CONNECTION: "NGX2CONNECTION",
  RADIONODE: "RADIONODE",
  LTECELL: "LTECELL",
  NRCELL: "NRCELL",
  SMTPEMAILS: "SMTPEMAILS",
  EMAILGROUPS: "EMAILGROUPS",
  COMMISSIONING: "COMMISSIONING",
  IMPORTMODAL: "IMPORTMODAL",
  IMPORTTEMPLATEINTASK: "IMPORTTEMPLATEINTASK",
  REMSCANSTART: "REMSCANSTART",
  REMSCANSTOP: "REMSCANSTOP",
  GLOBALTEMPLATE: "GLOBALTEMPLATE",
  LOCALTEMPLATE: "LOCALTEMPLATE",
  ADDDOMAIN: "ADDDOMAIN",
  ADDREGION: "ADDREGION",
  DELETEREGION: "DELETEREGION",
  APPVERSION: "APPVERSION",
  DOMAIN: "DOMAIN",
  REGION: "REGION",
  UPGRADE: "UPGRADE",
  SNMPVERSION: "SNMPVERSION",
  DELETETEMPLATE: "DELETETEMPLATE",
  LOCALPANELEMPLATE: "LOCALPANELEMPLATE",
  ALARMTRACKID: "ALARMTRACKID",
  DELETEGROUP: "DELETEGROUP",
  CELLDELETE: "CELLDELETE",
  CUSTOMEVENT: "CUSTOMEVENT",
  USERFILEMGMT: "USERFILEMGMT",
  EXTERNALUSERFILEMGMT: "EXTERNALUSERFILEMGMT",
  PROVISIONING: "PROVISIONING",
  DUPROVISION: "DUPROVISION",
  NODEFILEMGMT: "NODEFILEMGMT",
  ADDSECTORHUB: "ADDSECTORHUB",
  MIGRATION: "MIGRATION",
  MIGRATESITETOREGION: "MIGRATESITETOREGION",
  TASKFAILDIALOG: "TASKFAILDIALOG",
  AUDIT: "AUDIT",
  TRAPRECEIVE: "TRAPRECEIVE",
  SYSLOGFORWARDING: "SYSLOGFORWARDING",
  RNCONFIGS: "RNCONFIGS",
  RNCUCONFIGS: "RNCUCONFIGS",
  RNCUPROVISION: "RNCUPROVISION",
  REMOVESNZONE: "REMOVESNZONE",
  REMOVECUZONE: "REMOVECUZONE",
  PROVISIONRN: "PROVISIONRN",
  ZONEDELETE: "ZONEDELETE",
  ENABLE911: "ENABLE911",
  RNCELLDELETEPOPUP: "RNCELLDELETEPOPUP",
  REMOVELTEZONEPOPUP: "REMOVELTEZONEPOPUP",
  DELETEEXTERNALUSER: "DELETEEXTERNALUSER",
  CELLKPILTECHART: "CELLKPILTECHART",
  ADDSITEPOPUP: "ADDSITEPOPUP",
  BULKSTATUSPOP: "BULKSTATUSPOP",
  THRESHOLDCROSSINGSETTINGPROFILE: "THRESHOLDCROSSINGSETTINGPROFILE",
  DELETETHRESHOLDCROSSINGSETTINGPROFILE: "DELETETHRESHOLDCROSSINGSETTINGPROFILE",
  ENABLETHRESHOLDCROSSINGSETTINGPROFILE: "ENABLETHRESHOLDCROSSINGSETTINGPROFILE",
  THRESHOLDCROSSINGEDITPROFILE: "THRESHOLDCROSSINGEDITPROFILE",
  THRESHOLDCROSSINGSETTINGACTION: "THRESHOLDCROSSINGSETTINGACTION",
  DELETETHRESHOLDCROSSINGSETTINGACTION: "DELETETHRESHOLDCROSSINGSETTINGACTION",
  THRESHOLDCROSSINGSETTINGSCHEDULE: "THRESHOLDCROSSINGSETTINGSCHEDULE",
  DELETETHRESHOLDCROSSINGSETTINGSCHEDULE: "DELETETHRESHOLDCROSSINGSETTINGSCHEDULE",
  THRESHOLDCROSSINGSETTINGRULE: "THRESHOLDCROSSINGSETTINGRULE",
  DELETETHRESHOLDCROSSINGSETTINGRULE: "DELETETHRESHOLDCROSSINGSETTINGRULE",
  ADDTHRESHOLDDEVICE: "ADDTHRESHOLDDEVICE",
  ADDNOTIFICATIONDEVICE: "ADDNOTIFICATIONDEVICE",
  REMOVENOTIFICATIONDEVICE: "REMOVENOTIFICATIONDEVICE",
  REMOVENOTIFICATIONDEVICES: "REMOVENOTIFICATIONDEVICES",
  REMOVETHRESHOLDDEVICES: "REMOVETHRESHOLDDEVICES",
  REMOVETHRESHOLDDEVICE: "REMOVETHRESHOLDDEVICE",
  ADDTHRESHOLDPROFILE: "ADDTHRESHOLDPROFILE",
  ADDNOTIFICATIONPROFILE: "ADDNOTIFICATIONPROFILE",
  REMOVETHRESHOLDPROFILES: "REMOVETHRESHOLDPROFILES",
  REMOVETHRESHOLDPROFILE: "REMOVETHRESHOLDPROFILE",
  ADDNOTES: "ADDNOTES",
  DELETENOTE: "DELETENOTE",
  ADDLABEL: "ADDLABEL",
  DELETELABEL: "DELETELABEL",
  ADDLABELDEVICE: "ADDLABELDEVICE",
  REMOVELABELDEVICE: "REMOVELABELDEVICE",
  REMOVELABELDEVICES: "REMOVELABELDEVICES",
  REMOVETHRESHOLDCROSSINGPROFILE: "REMOVETHRESHOLDCROSSINGPROFILE",
  ADDTHRESHOLDCROSSINGPROFILE: "ADDTHRESHOLDCROSSINGPROFILE",
  ADDUSER: "ADDUSER",
  DELETEUSER: "DELETEUSER",
};

export const DEVICESTATUS: { [key: string]: string } = {
  TIMEOUT: "19", //Disconnected
  MAINTENANCE_MODE: "18",
  SPPROFILE_RUNNING: "20",
  NORMAL: "0", //connected
  MINOR: "1",
  MAJOR: "2",
  CRITICAL: "3",
};

export const SCOS_VERSION_23_4_0: string = "23.4.0";

export const PROVISION_CHECK_VAL: number = 2048;

export const PERFORMANCENETWORKLIST: Array<string> = ["LTE", "NR"];
export const PERFORMANCETIMELIST: Array<string> = ["LAST 1 HOUR", "LAST 15 MINS", "LAST 24 HOURS"]

export const PerformanceLteData = [
  {
    id: 1,
    name: "Data Retainability",
    green: ">=99.5%",
    yellow: "99.5% > X > 99%",
    red: "<99%",
  },
  {
    id: 2,
    name: "Volte Retainability",
    green: ">=98.5%",
    yellow: "98.5% < X < 97.5%",
    red: "<=97.5%",
  },
  {
    id: 3,
    name: "Data Accessibility",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<=98%",
  },
  {
    id: 4,
    name: "Volte Accessibility",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<=98%",
  },
  {
    id: 5,
    name: "HO Success Rate",
    green: ">=98%",
    yellow: "98% > X > 95%",
    red: "<=95%",
  },
]

export const PerformanceNRData = [
  {
    id: 1,
    name: "ENDC Data Retainability",
    green: ">=99.5%",
    yellow: "99.5% > X > 99%",
    red: "<99%",
  },
  {
    id: 2,
    name: "ENDC UE Retainability",
    green: ">=98.5%",
    yellow: "98.5% < X < 97.5%",
    red: "<=97.5%",
  },
  {
    id: 3,
    name: "ENDC Data Accessibility",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<=98%",
  },
  {
    id: 4,
    name: "ENDC UE Accessibility",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<=98%",
  },
  {
    id: 5,
    name: "HO Success Rate",
    green: ">=98%",
    yellow: "98% > X > 95%",
    red: "<=95%",
  },
]

export const lteSizeData = [
  {
    id: 1,
    name: "ERAB Establishment SR",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<=98%",
  },
  {
    id: 2,
    name: "VoLTE Establishment SR",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<=98%",
  },
  {
    id: 3,
    name: "RRC Connection Establishment SR",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<=98%",
  },
  {
    id: 4,
    name: "ERAB Drop Rate",
    green: "<=0.5%",
    yellow: "0.5% < X < 1%",
    red: ">=1%",
  },
  {
    id: 5,
    name: "VoLTE Drop Rate",
    green: "<=1%",
    yellow: "1% < X < 2%",
    red: ">=2%",
  },
  {
    id: 6,
    name: "HandIn SR",
    green: ">=98%",
    yellow: "98% > X > 95%",
    red: "<=95%",
  },
  // {
  //   id: 7,
  //   name: "HandOut Success Rate",
  //   green: ">=98%",
  //   yellow: "98% > X > 95%",
  //   red: "<=95%",
  // },
  // {
  //   id: 8,
  //   name: "Intra-frequency X1-HO SR",
  //   green: ">=99%",
  //   yellow: "99% > X > 98%",
  //   red: "<=98%",
  // },
  {
    id: 9,
    name: "Inter-frequency X1-HO SR",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<=98%",
  },
  {
    id: 10,
    name: "LTE Cell Availability",
    green: ">=99.5%",
    yellow: "99.5% > X > 99%",
    red: "<=99%",
  },
  {
    id: 11,
    name: "SgNB Addition SR",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<= 98%",
  },
];

export const nrSizeData = [
  {
    id: 1,
    name: "SgNB Addition SR",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<=98%",
  },
  {
    id: 2,
    name: "ENDC UE Session Drop Rate",
    green: "<=0.5%",
    yellow: "0.5% < X < 1%",
    red: ">=1%",
  },
  {
    id: 3,
    name: "Intra CU Handover SR",
    green: ">=99%",
    yellow: "99% > X > 98%",
    red: "<=98%",
  },
  {
    id: 4,
    name: "NR Cell Availability",
    green: ">=99.5%",
    yellow: "99.5% > X > 99%",
    red: "<=99%",
  },
]

export const USERPASSWORD_MAXLENGTH: number = 100;

export const NODE_CLI_PASSWARD_MAXLENGTH: number = 128;
export const NODE_LCI_PASSWARD_MAXLENGTH: number = 32;
export const TYPETHRESHOLDLIST: Array<string> = ["type1", "type2", "type3", "type4", "type5"];
export const REGIONTHRESHOLDLIST: Array<string> = ["REGION1", "REGION2"];
export const GROUPTHRESHOLDLIST: Array<string> = ["GROUP1", "GROUP2"];

export const LteKpiList: IKpi[] = [
  {
    "kpi": "ERAB Establishment SR",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  },
  {
    "kpi": "VoLTE Establishment SR",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  },
  {
    "kpi": "RRC Connection Establishment SR",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  },
  {
    "kpi": "ERAB Drop Rate",
    "thresholdAbove": true,
    "dataType": "float",
    "minValue": 0,
    "maxValue": 99
  },
  {
    "kpi": "VoLTE Drop Rate",
    "thresholdAbove": true,
    "dataType": "float",
    "minValue": 0,
    "maxValue": 99
  },
  {
    "kpi": "HandIn Over S1 Interface SR",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  },
  {
    "kpi": "HandOut Over S1 Interface SR",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  },
  {
    "kpi": "Intra-Frequency X1 HO SR",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  },
  {
    "kpi": "Inter-Frequency X1 HO SR",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  },
  {
    "kpi": "LTE Cell Availability",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  }
]

export const NrKpiList: IKpi[] = [
  {
    "kpi": "SgNB Addition SR",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  },
  {
    "kpi": "ENDC UE session drop rate",
    "thresholdAbove": true,
    "dataType": "float",
    "minValue": 0,
    "maxValue": 99
  },
  {
    "kpi": "Intra CU Handover Success Rate",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  },
  {
    "kpi": "NR Cell Availability",
    "thresholdAbove": false,
    "dataType": "float",
    "minValue": 1,
    "maxValue": 100
  }
]
